import { useEffect, useId, useRef } from "react";
import { ArrowUpRight, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { CivilShelterInfo } from "./MapModel";
import ShelterDetails from "./ShelterDetails";
import { ShelterClose, ShelterHeader, ShelterNavigation, ShelterOverlay, ShelterPanel, ShelterTitle } from "./ShelterInfoModalStyled";

interface ShelterInfoModalProps {
  title: string;
  address: string;
  shelter?: CivilShelterInfo;
  latitude: number;
  longitude: number;
  onClose: () => void;
}

const ShelterInfoModal = ({ title, address, shelter, latitude, longitude, onClose }: ShelterInfoModalProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
      }
      if (event.key !== "Tab") return;
      const controls = Array.from(panelRef.current?.querySelectorAll<HTMLElement>("button, a[href]") || []);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  return <ShelterOverlay onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <ShelterPanel ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <ShelterHeader>
        <span>{t("map.modal.pointOfInterest")}</span>
        <ShelterClose ref={closeRef} type="button" onClick={onClose} aria-label={t("map.modal.close")}><X size={24} aria-hidden="true" /></ShelterClose>
      </ShelterHeader>
      <ShelterTitle id={titleId}>{title}</ShelterTitle>
      <ShelterDetails shelter={shelter} address={address} />
      <ShelterNavigation href={`https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`} target="_blank" rel="noopener noreferrer">
        {t("map.modal.navigate")}<ArrowUpRight size={20} aria-hidden="true" />
      </ShelterNavigation>
    </ShelterPanel>
  </ShelterOverlay>;
};

export default ShelterInfoModal;
